// Template dependencies
import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { ReplaySubject } from 'rxjs';

import {
    API_TOOLS_OPTIONS,
    API_V4_TOOLS_OPTIONS,
    FREE_TRIAL_ITEM,
    HEADER_MAIN_MENU_OPTIONS,
    PRO_ADMIN_TIER,
    SIGN_IN_ITEM,
    SUPPORT_INDICATOR_DATA_MANAGERS_OPTIONS,
    SUPPORT_LIST_OPTIONS,
    SUPPORT_SYSTEMS_OPTIONS,
    SUPPORT_Y_DASHBOARD_OPTIONS,
    TOOLS_OPTIONS,
} from './constants';
import { API_FEATURE, API_V4_FEATURE } from 'EntitlementsApp/ts/features/feature-needed/feature-needed.constants';
import { SAVED_ITEMS_ICONS_SUFFIX } from 'SearchApp/ts/constants';
import { AutocompleteExtraLink } from 'SearchApp/ts/types/categories-autocomplete-results.type';
import { AdditionalNavMenuItem } from 'Angular/features/components/navigation/interfaces/additional-nav-menu-item.interface';
import {
    NavMenuColumn,
    NavMenuContent,
    NavMenuList,
} from 'Angular/features/components/navigation/interfaces/nav-menu-list.interface';
import { AngularUser } from 'Angular/interfaces/angular-user.interface';
import { UserSavedItem, UserVisitedSecurity } from 'SearchApp/ts/interfaces/autocomplete-result.interface';

import { FeatureService } from 'EntitlementsApp/ts/features/feature-needed/feature.service';

import { NavigationInternalsModule } from 'Angular/features/components/navigation/navigation-internals.module';
import { IconModule } from 'Angular/features/directives/icon/icon.module';
import { TooltipModule } from 'Angular/features/directives/popovers/tooltip/tooltip.module';
import { TopBarNavAutocompleteModule } from 'SearchApp/ts/features/top-bar-nav-autocomplete/top-bar-nav-autocomplete.module';

import { TopBarNavAutocompleteComponent } from 'SearchApp/ts/features/top-bar-nav-autocomplete/top-bar-nav-autocomplete.component';
import { isDesktop as isDesktopMethod } from 'Angular/utils/media';

/**
 *
 * Top Bar Nav Component
 *
 * Highest level component for our top navigation bar.
 *
 */

@Component({
    selector: 'ycn-top-bar-nav',
    templateUrl: './top-bar-nav.template.html',
    imports: [CommonModule, IconModule, NavigationInternalsModule, TooltipModule, TopBarNavAutocompleteModule],
})
export class TopBarNavComponent implements OnInit {
    @ViewChild(TopBarNavAutocompleteComponent) topBarNavAutocomplete!: TopBarNavAutocompleteComponent;

    overrideHeaderConfig: string[];

    totalLockout: boolean;

    USER: AngularUser = window.USER;

    nextLoginLink: string;

    formattedUserText: string = window.USER.firstName ?? window.USER.email;

    scheduleDemoText: string = HEADER_MAIN_MENU_OPTIONS.SCHEDULE_DEMO.title;

    errorMessages: string[] = [];

    // header config
    headerMainMenuConfig: NavMenuList[] = [];

    // menu options
    FREE_TRIAL_ITEM: AdditionalNavMenuItem = FREE_TRIAL_ITEM;

    SIGN_IN_ITEM: AdditionalNavMenuItem = SIGN_IN_ITEM;

    // booleans
    chatEnabled: boolean = false;

    mainMenuActive: boolean = false;

    searchActive: boolean = false;

    searchMenuActive: boolean = false;

    showTrial: boolean;

    userMenuActive: boolean = false;

    isDesktop: boolean = false;

    contentLinks: NavMenuContent[] = [];

    defaultResults: AutocompleteExtraLink[] = [];

    constructor(private eltRef: ElementRef, private featureService: FeatureService) {
        this.showTrial = (this.USER.authenticated && !this.USER.hasUpgraded) || !this.USER.authenticated;
        this.nextLoginLink = `/login?next=${window.location.pathname}`;
        this.totalLockout = this.eltRef.nativeElement.getAttribute('totalLockout') || false;
        this.overrideHeaderConfig = JSON.parse(this.eltRef.nativeElement.getAttribute('overrideHeaderConfig')) || [];
        const headerContent = JSON.parse(eltRef.nativeElement.getAttribute('headerContent')) || {};
        this.contentLinks = headerContent.support_content || [];
        const userLastSavedItems: AutocompleteExtraLink[] = (
            (headerContent.user_last_saved_items as UserSavedItem[]) || []
        ).map(({ url, name, type, features_needed }) => ({
            label: name,
            url,
            icon: `file-${SAVED_ITEMS_ICONS_SUFFIX[type]}`,
            features_needed,
            suffix: SAVED_ITEMS_ICONS_SUFFIX[type],
            type: 'asset',
        }));

        const userVisitedSecurities: AutocompleteExtraLink[] = (
            (headerContent.last_visited_securities as UserVisitedSecurity[]) || []
        ).map(({ url, name, features_needed, display_security_id }) => ({
            label: display_security_id,
            url,
            features_needed,
            description: name,
            type: 'security',
        }));
        this.defaultResults = [...userVisitedSecurities, ...userLastSavedItems];
    }

    ngOnInit(): void {
        this.addWidthAsBodyStyle();
        // Before loading remove the preloaded header placeholder.
        const placeholder = document.getElementById('header-placeholder');
        if (placeholder && placeholder.parentNode) {
            // Call `removeChild()` since IE does not support `remove()`.
            placeholder.parentNode.removeChild(placeholder);
        }
        this.headerMainMenuConfig = this.getHeaderMainMenuConfig();

        // Other components that show a button that opens HubSpot chat (for example SupportContactInfoPageComponent)
        // cannot rely on hsConversationsOnReady to determine when the chat is ready, because
        // the component may be loaded after functions in the hsConversationsOnReady array have already been executed.
        // TopBarNavComponent loads before other components, so it's not an issue here.
        // Functions in hsConversationsOnReady are only executed once, so other components besides TopBarNavComponent need this
        // subject to know whether functions in hsConversationsOnReady have been executed as a way to determine whether the chat
        // button should be shown.
        window.hsChatEnabledSubject = new ReplaySubject<void>();

        // The window.hsConversationsOnReady is not always present on load since not
        // all users have access to our chat (i.e. Students or Users who have has_chat set
        // to False). If the hsConversationsOnReady array is available we know we can add a
        // callback function to show the chat.
        // it is possible that the ready event has already been fired, if the HubSpotConversations
        // object is set then we know hsConversationsOnReady already happened
        const enableChat: () => void = () => {
            this.chatEnabled = true;
            window.hsChatEnabledSubject.next();
        };
        if (window.HubSpotConversations) {
            enableChat();
        } else if (window.hsConversationsOnReady !== undefined) {
            window.hsConversationsOnReady.push(enableChat);
        }

        // open menu by default if user on PRO_ADMIN tier (impersonation only)
        this.userMenuActive = this.USER.tierInternalName === PRO_ADMIN_TIER;
    }

    getHeaderMainMenuConfig(): NavMenuList[] {
        /**
         * Dynamically sets the content of the navigation menu, depending on properties of USER such as:
         *  - their features
         *  - their missing feature view preference
         *  - the groups they belong to
         *  - whether they are authenticated
         *  - whether they have upgraded or are on a free trial
         *  - Their content links
         */

        // Set the dropdown for the Data tab
        const headerDataMenu = { ...HEADER_MAIN_MENU_OPTIONS.DATA };
        headerDataMenu.columns = this.addLockToMenuItems(
            this.filterHiddenMenuItems(HEADER_MAIN_MENU_OPTIONS.DATA.columns!)
        );
        headerDataMenu.content = this.contentLinks.find((item) => item.tab.toUpperCase() === 'DATA');

        // Set the columns for the Tools tab
        const toolsListColumns = this.addLockToMenuItems(this.filterHiddenMenuItems(TOOLS_OPTIONS));
        if (this.featureService.userHasFeatures([API_FEATURE])) {
            toolsListColumns[toolsListColumns.length - 1].list.push(...API_TOOLS_OPTIONS);

            if (this.featureService.userHasFeatures([API_V4_FEATURE])) {
                // Both API versions tool options include a divider. If both are shown, don't push the second divider.
                toolsListColumns[toolsListColumns.length - 1].list.push(API_V4_TOOLS_OPTIONS[1]);
            }
        } else if (this.featureService.userHasFeatures([API_V4_FEATURE])) {
            toolsListColumns[toolsListColumns.length - 1].list.push(...API_V4_TOOLS_OPTIONS);
        }

        const headerToolsMenu = Object.assign([], HEADER_MAIN_MENU_OPTIONS.TOOLS);
        headerToolsMenu.columns = toolsListColumns;
        headerToolsMenu.content = this.contentLinks.find((item) => item.tab.toUpperCase() === 'TOOLS');

        // Set the dropdown for the Support tab
        let supportListOptions: NavMenuColumn[] = Object.assign([], SUPPORT_LIST_OPTIONS);
        supportListOptions = this.addLockToMenuItems(this.filterHiddenMenuItems(supportListOptions));
        if (this.USER.groups.includes('systems')) {
            supportListOptions[supportListOptions.length - 1].list.push(...SUPPORT_SYSTEMS_OPTIONS);
        }
        if (this.USER.groups.includes('indicator_data_managers')) {
            supportListOptions[supportListOptions.length - 1].list.push(...SUPPORT_INDICATOR_DATA_MANAGERS_OPTIONS);
        }
        if (this.USER.groups.includes('y_dashboard')) {
            supportListOptions[supportListOptions.length - 1].list.push(...SUPPORT_Y_DASHBOARD_OPTIONS);
        }
        const headerSupportMenu = { ...HEADER_MAIN_MENU_OPTIONS.SUPPORT };
        headerSupportMenu.columns = supportListOptions;
        headerSupportMenu.content = this.contentLinks.find((item) => item.tab.toUpperCase() === 'SUPPORT');

        // Set the main menu config using the three dropdowns
        let headerMainMenuConfig = [headerDataMenu, headerToolsMenu, headerSupportMenu];

        // Add Plans / Free Trial links if necessary
        if (this.overrideHeaderConfig.length > 0) {
            headerMainMenuConfig = [];
            this.overrideHeaderConfig.forEach((itemName) => {
                if (itemName === 'TOOLS') {
                    headerMainMenuConfig.push(headerToolsMenu);
                } else if (itemName === 'SUPPORT') {
                    headerMainMenuConfig.push(headerSupportMenu);
                } else if (itemName === 'DATA') {
                    headerMainMenuConfig.push(headerDataMenu);
                } else {
                    headerMainMenuConfig.push(HEADER_MAIN_MENU_OPTIONS[itemName]);
                }
            });
        } else if (this.USER.authenticated && !this.USER.hasUpgraded) {
            headerMainMenuConfig.push(HEADER_MAIN_MENU_OPTIONS.TRIAL_WALL);
            headerMainMenuConfig.push(HEADER_MAIN_MENU_OPTIONS.FREE_TRIAL);
            // authenticated but days remaining in trial, link to plans
        } else if (this.USER.authenticated && !!this.USER.daysRemainingInTrial) {
            headerMainMenuConfig.push(HEADER_MAIN_MENU_OPTIONS.TRIAL_WALL);
            // Not authenticated, link to plans / trial / sign in
        } else if (!this.USER.authenticated) {
            headerMainMenuConfig.push(HEADER_MAIN_MENU_OPTIONS.PLANS);
            // Set ?next= path for SIGN_IN href
        }

        return headerMainMenuConfig;
    }

    toggleMainMenuActive(): void {
        this.mainMenuActive = !this.mainMenuActive;
        this.searchMenuActive = false;
        this.userMenuActive = false;
    }

    setUserMenuActive(active: boolean): void {
        this.userMenuActive = active;
        this.mainMenuActive = false;
        this.searchActive = false;
    }

    searchActiveOn(): void {
        this.searchActive = true;
    }

    searchActiveOff(): void {
        this.searchActive = false;
        this.topBarNavAutocomplete.reset();
    }

    scheduleDemo(e: Event): void {
        const demoUrl = `${HEADER_MAIN_MENU_OPTIONS.SCHEDULE_DEMO.href}?id=${this.USER.email}`;
        window.open(demoUrl, '_blank');
        e.preventDefault();
    }

    private addLockToMenuItems(menuColumns: NavMenuColumn[]): NavMenuColumn[] {
        return menuColumns.map((column) => ({
            ...column,
            list: column.list.map((menuItem) => {
                const featuresRequired = menuItem.features || [];
                const hasFeatures = menuItem.someFeatures
                    ? this.featureService.userHasSomeFeatures(featuresRequired)
                    : this.featureService.userHasFeatures(featuresRequired);
                const missingFeaturePreference =
                    menuItem.overrideMissingFeaturePreference || this.USER.missingFeaturePreference;
                return {
                    ...menuItem,
                    showLock: menuItem.type !== 'divider' && !hasFeatures && missingFeaturePreference === 'upsell',
                };
            }),
        }));
    }

    /**
     * Filters out menu items that the user does not have access to if the user has hide as their missing feature view
     * preference.
     */
    private filterHiddenMenuItems(menuColumns: NavMenuColumn[]): NavMenuColumn[] {
        if (this.featureService.allFeaturesAvailable) {
            return menuColumns;
        }

        return menuColumns
            .filter((column) => {
                const columnFeatures = column.features || [];
                return this.featureService.userHasFeatures(columnFeatures);
            })
            .map((column) => {
                let lastPushWasDivider = false;
                return {
                    ...column,
                    list: column.list.filter((menuItem) => {
                        const featuresRequired = menuItem.features || [];
                        const isDivider = menuItem.type === 'divider';

                        const hasFeatures = menuItem.someFeatures
                            ? this.featureService.userHasSomeFeatures(featuresRequired)
                            : this.featureService.userHasFeatures(featuresRequired);

                        const missingFeaturePreference =
                            menuItem.overrideMissingFeaturePreference || this.USER.missingFeaturePreference;

                        const showFeature = hasFeatures || missingFeaturePreference !== 'hide';
                        const includeItem = (isDivider && !lastPushWasDivider) || (!isDivider && showFeature);
                        lastPushWasDivider = isDivider;

                        return includeItem;
                    }),
                };
            })
            .filter((column) => column.list.length > 0);
    }

    @HostListener('window:resize', ['$event'])
    updateLegendPosition(): void {
        this.isDesktop = isDesktopMethod();
        this.addWidthAsBodyStyle();
    }

    /**
     * There is a --body-width CSS variable that needs this
     */
    private addWidthAsBodyStyle(): void {
        const body = document.querySelector('body');
        if (body) {
            body.style.setProperty('--body-width', `${body.clientWidth}px`);
        }
    }

    addErrorMessage(message: string): void {
        this.errorMessages.push(message);
    }

    removeErrorMessage(index: number): void {
        this.errorMessages.splice(index, 1);
    }
}
